import type { UserWithAuthData } from '@/types/auth';
import { selectUser, useAuthStore } from '@/store';

declare global {
	interface Window {
		dataLayer: unknown[];
		gtag?: (...args: unknown[]) => void;
	}
}

type AnalyticsPrimitive = string | number | boolean;
type AnalyticsParams = Record<string, unknown>;
type PageExitReason = 'route_change' | 'pagehide';

interface PageHistoryEntry {
	path: string;
	title: string;
	durationSeconds: number;
	enteredAt: number;
}

interface LongestPageEntry {
	path: string;
	title: string;
	durationSeconds: number;
	updatedAt: number;
}

interface ActivePageEntry {
	path: string;
	title: string;
	enteredAt: number;
}

const GA_MEASUREMENT_ID =
	import.meta.env.VITE_GA_MEASUREMENT_ID || 'G-1V6WC7NWF8';
const GA_DEBUG_MODE_ENABLED = import.meta.env.VITE_GA_DEBUG_MODE === 'true';
const GA_SCRIPT_ID = 'hv360-ga-script';
const PAGE_HISTORY_STORAGE_KEY = 'hv360-ga-page-history';
const LONGEST_PAGE_STORAGE_KEY = 'hv360-ga-longest-page';
const MAX_PAGE_HISTORY = 25;
const MAX_HISTORY_PREVIEW = 10;
const PAGE_VIEW_DEDUPE_WINDOW_MS = 1000;
const MAX_EVENT_NAME_LENGTH = 40;

let isAnalyticsInitialized = false;
let hasPageLifecycleListener = false;
let activePage: ActivePageEntry | null = null;
let lastTrackedPagePath = '';
let lastTrackedPageAt = 0;

const isBrowser = typeof window !== 'undefined';

const normalizeString = (value: unknown, maxLength = 250) => {
	if (value == null) {
		return undefined;
	}

	const normalized = String(value).trim();
	return normalized ? normalized.slice(0, maxLength) : undefined;
};

const pickFirstNormalizedString = (
	values: unknown[],
	maxLength = 250,
): string | undefined => {
	for (const value of values) {
		const normalizedValue = normalizeString(value, maxLength);
		if (normalizedValue) {
			return normalizedValue;
		}
	}

	return undefined;
};

const normalizeValue = (value: unknown): AnalyticsPrimitive | undefined => {
	if (value == null) {
		return undefined;
	}

	if (typeof value === 'string') {
		return normalizeString(value, 500);
	}

	if (typeof value === 'number') {
		return Number.isFinite(value) ? value : undefined;
	}

	if (typeof value === 'boolean') {
		return value;
	}

	if (Array.isArray(value)) {
		const normalizedItems = value
			.map((item) => normalizeString(item))
			.filter(Boolean);

		return normalizedItems.length
			? normalizedItems.join(' | ').slice(0, 500)
			: undefined;
	}

	if (value instanceof Date) {
		return value.toISOString();
	}

	if (typeof value === 'object') {
		try {
			return JSON.stringify(value).slice(0, 500);
		} catch {
			return undefined;
		}
	}

	return undefined;
};

const readStorageValue = <T,>(key: string): T | null => {
	if (!isBrowser) {
		return null;
	}

	try {
		const rawValue = sessionStorage.getItem(key);
		return rawValue ? (JSON.parse(rawValue) as T) : null;
	} catch {
		return null;
	}
};

const writeStorageValue = (key: string, value: unknown) => {
	if (!isBrowser) {
		return;
	}

	try {
		sessionStorage.setItem(key, JSON.stringify(value));
	} catch {
		// Ignore storage failures and continue tracking events.
	}
};

const getPageHistory = () =>
	readStorageValue<PageHistoryEntry[]>(PAGE_HISTORY_STORAGE_KEY) || [];

const setPageHistory = (history: PageHistoryEntry[]) => {
	writeStorageValue(PAGE_HISTORY_STORAGE_KEY, history.slice(-MAX_PAGE_HISTORY));
};

const getLongestPage = () =>
	readStorageValue<LongestPageEntry>(LONGEST_PAGE_STORAGE_KEY);

const setLongestPage = (page: LongestPageEntry) => {
	writeStorageValue(LONGEST_PAGE_STORAGE_KEY, page);
};

const buildPageHistoryPreview = (paths: string[]) =>
	paths.slice(-MAX_HISTORY_PREVIEW).join(' > ');

const normalizeEventName = (value: string) => {
	const normalized = String(value || '')
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9_]+/g, '_')
		.replace(/^_+|_+$/g, '')
		.replace(/_+/g, '_');

	if (!normalized) {
		return 'custom_event';
	}

	const eventName = /^[a-z]/.test(normalized)
		? normalized
		: `event_${normalized}`;

	return eventName.slice(0, MAX_EVENT_NAME_LENGTH);
};

const getCurrentPath = () =>
	normalizeString(
		isBrowser
			? `${window.location.pathname}${window.location.search}`
			: undefined,
		500,
	);

const getCurrentTitle = () =>
	normalizeString(isBrowser ? document.title || 'HealthView 360' : undefined, 500);

const getDefaultSourceModule = () => {
	if (!isBrowser) {
		return undefined;
	}

	const [firstSegment] = window.location.pathname.split('/').filter(Boolean);
	return normalizeString(firstSegment?.replace(/-/g, '_') || 'app', 100);
};

const getUserContext = (
	user: UserWithAuthData | null = selectUser(useAuthStore.getState()),
) => {
	const authUser = user || selectUser(useAuthStore.getState());
	const userInfo = authUser?.userInformation;
	const fullName = pickFirstNormalizedString(
		[
			authUser?.name,
			(userInfo as { fullName?: string } | undefined)?.fullName,
			[userInfo?.firstName, userInfo?.lastName].filter(Boolean).join(' '),
			userInfo?.email,
			authUser?.email,
		],
		250,
	);
	const email = pickFirstNormalizedString(
		[
			authUser?.email,
			userInfo?.email,
		],
		250,
	);
	const userId = pickFirstNormalizedString(
		[
			userInfo?._id,
			(authUser as { id?: string } | null)?.id,
			authUser?.oid,
		],
		250,
	);
	const userRole = pickFirstNormalizedString(
		[
			userInfo?.role,
			(authUser as { role?: string } | null)?.role,
		],
		250,
	);
	const userPort = pickFirstNormalizedString(
		[
			userInfo?.portsAvailable,
			(authUser as { portsAvailable?: string } | null)?.portsAvailable,
		],
		250,
	);

	return {
		username: fullName,
		useremail: email,
		email,
		id: userId,
		userId,
		user_role: userRole,
		userRole,
		user_port: userPort,
		userPort,
		current_path: getCurrentPath(),
		currentPath: getCurrentPath(),
		current_title: getCurrentTitle(),
		currentTitle: getCurrentTitle(),
	};
};

const buildEventParams = (params: AnalyticsParams = {}) => {
	const combinedParams = {
		...getUserContext(),
		...(GA_DEBUG_MODE_ENABLED ? { debug_mode: true } : {}),
		...params,
	};

	return Object.entries(combinedParams).reduce<
		Record<string, AnalyticsPrimitive>
	>((accumulator, [key, value]) => {
		const normalizedValue = normalizeValue(value);
		if (normalizedValue !== undefined) {
			accumulator[key] = normalizedValue;
		}
		return accumulator;
	}, {});
};

export const initializeAnalytics = () => {
	if (!isBrowser || isAnalyticsInitialized) {
		return;
	}

	window.dataLayer = window.dataLayer || [];
	window.gtag =
		window.gtag ||
		(function () {
			window.dataLayer.push(arguments);
		} as (...args: unknown[]) => void);

	if (!document.getElementById(GA_SCRIPT_ID)) {
		const script = document.createElement('script');
		script.id = GA_SCRIPT_ID;
		script.async = true;
		script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
		document.head.appendChild(script);
	}

	window.gtag('js', new Date());
	window.gtag('config', GA_MEASUREMENT_ID, {
		send_page_view: false,
		transport_type: 'beacon',
	});

	isAnalyticsInitialized = true;
};

export const syncAnalyticsUser = (
	user: UserWithAuthData | null = selectUser(useAuthStore.getState()),
) => {
	if (!isBrowser) {
		return;
	}

	initializeAnalytics();

	const userContext = getUserContext(user);

	window.gtag?.('config', GA_MEASUREMENT_ID, {
		send_page_view: false,
		user_id: userContext.id,
	});

	window.gtag?.('set', 'user_properties', {
		username: userContext.username || '',
		useremail: userContext.useremail || '',
		email: userContext.email || '',
		id: userContext.id || '',
		userId: userContext.userId || '',
		user_role: userContext.user_role || '',
		userRole: userContext.userRole || '',
		user_port: userContext.user_port || '',
		userPort: userContext.userPort || '',
	});
};

export const trackEvent = (eventName: string, params: AnalyticsParams = {}) => {
	if (!isBrowser) {
		return;
	}

	initializeAnalytics();
	window.gtag?.('event', normalizeEventName(eventName), buildEventParams(params));
};

export const trackCoreClick = (
	actionName: string,
	params: AnalyticsParams = {},
) => {
	const sourceModule =
		normalizeString(params.source_module, 100) ||
		normalizeString(params.sourceModule, 100) ||
		getDefaultSourceModule();

	trackEvent(actionName, {
		action_name: actionName,
		actionName: actionName,
		event_group: 'core_click',
		source_module: sourceModule,
		sourceModule: sourceModule,
		...params,
	});
};

const flushActivePage = (reason: PageExitReason) => {
	if (!activePage) {
		return;
	}

	const durationSeconds = Math.max(
		1,
		Math.round((Date.now() - activePage.enteredAt) / 1000),
	);

	const pageHistoryEntry: PageHistoryEntry = {
		path: activePage.path,
		title: activePage.title,
		durationSeconds,
		enteredAt: activePage.enteredAt,
	};

	const updatedHistory = [...getPageHistory(), pageHistoryEntry].slice(
		-MAX_PAGE_HISTORY,
	);
	const historyPreview = buildPageHistoryPreview(
		updatedHistory.map((entry) => entry.path),
	);

	setPageHistory(updatedHistory);

	const previousLongestPage = getLongestPage();
	const nextLongestPage =
		!previousLongestPage ||
		durationSeconds >= previousLongestPage.durationSeconds
			? {
					path: pageHistoryEntry.path,
					title: pageHistoryEntry.title,
					durationSeconds,
					updatedAt: Date.now(),
				}
			: previousLongestPage;

	if (
		!previousLongestPage ||
		nextLongestPage.durationSeconds !== previousLongestPage.durationSeconds ||
		nextLongestPage.path !== previousLongestPage.path
	) {
		setLongestPage(nextLongestPage);
		trackEvent('longest_page_updated', {
			page_path: nextLongestPage.path,
			page_title: nextLongestPage.title,
			duration_seconds: nextLongestPage.durationSeconds,
			page_history: historyPreview,
		});
	}

	trackEvent('page_engagement_summary', {
		page_path: pageHistoryEntry.path,
		page_title: pageHistoryEntry.title,
		duration_seconds: pageHistoryEntry.durationSeconds,
		exit_reason: reason,
		page_history: historyPreview,
		longest_page_path: nextLongestPage.path,
		longest_page_seconds: nextLongestPage.durationSeconds,
	});

	activePage = null;
};

export const registerPageLifecycleTracking = () => {
	if (!isBrowser || hasPageLifecycleListener) {
		return;
	}

	window.addEventListener('pagehide', () => {
		flushActivePage('pagehide');
	});

	hasPageLifecycleListener = true;
};

export const trackPageNavigation = (path: string, title: string) => {
	if (!isBrowser) {
		return;
	}

	initializeAnalytics();
	flushActivePage('route_change');

	const now = Date.now();
	if (
		lastTrackedPagePath === path &&
		now - lastTrackedPageAt < PAGE_VIEW_DEDUPE_WINDOW_MS
	) {
		activePage = {
			path,
			title,
			enteredAt: now,
		};
		return;
	}

	lastTrackedPagePath = path;
	lastTrackedPageAt = now;

	const currentHistory = getPageHistory();
	const pageHistoryPreview = buildPageHistoryPreview([
		...currentHistory.map((entry) => entry.path),
		path,
	]);
	const longestPage = getLongestPage();

	activePage = {
		path,
		title,
		enteredAt: now,
	};

	trackEvent('page_view', {
		page_path: path,
		page_title: title,
		page_location: window.location.href,
		page_referrer: document.referrer || '',
		history_depth: currentHistory.length + 1,
		page_history: pageHistoryPreview,
		longest_page_path: longestPage?.path || '',
		longest_page_seconds: longestPage?.durationSeconds || 0,
	});
};
